import { capitalize, isEmpty } from 'lodash';
import moment from 'moment';
import StringUtils from '../../common/services/StringUtils';
import DateTimeUtils from '../../common/services/DateTimeUtils';
import {
  getCurrentClinicTimeFormatFromSession,
  resolveClinicTimeFormat,
} from '../../common/services/ClinicTimeFormatUtils';

export default class Referral {

  constructor(referral) {
    this.referral = { ...referral };
    this.currentClinicTimeFormat = resolveClinicTimeFormat(
      this.referral?.current_clinic_time_format || getCurrentClinicTimeFormatFromSession()
    );

    this._hasMatchCache = { lastReferral: null, lastUser: null, lastResult: null };
  }

  hasReferrer() {
    return !!(this.referral && this.referral.referrer);
  }

  getFullName() {
    if (!this.referral.patient) return '';
    return this.referral.patient.full_name;
  }

  getMrn() {
    return this.referral.mrn;
  }

  getPatientDob() {
    const birthdate = this.referral?.patient?.details?.birthdate;
    if (birthdate && moment(birthdate, "YYYY-MM-DD", true).isValid()) {
      return moment(birthdate, "YYYY-MM-DD").format('DD MMM YYYY');
    }
    return '';
  }

  getDiagnosis() {
    return this.referral?.diagnosis?.description;
  }

  getPhone() {
    if (!this.referral.patient) return '';
    if (!Array.isArray(this.referral.patient.details)) {
      return `${this.referral.patient.details.phone}`;
    }
    return `${this.referral.patient.details[0].phone}`;
  }

  isUserReferrer() {
    const clinicUsers = this.referral?.referrer?.clinic_users;
    if (!Array.isArray(clinicUsers)) return false;
    const usersUUIDArr = clinicUsers.map(item => (item && item.user ? item.user.uuid : -1));
    return usersUUIDArr.indexOf(this.referral?.user_uuid) >= 0;
  }


  hasMatch(currentUser) {
    const referral = this.referral;

    if (
      this._hasMatchCache.lastReferral === referral &&
      this._hasMatchCache.lastUser === currentUser
    ) {
      return this._hasMatchCache.lastResult;
    }

    const refName = referral?.referrer?.name;
    const currentClinicName = currentUser?.clinics?.[0]?.name;

    const result =
      Array.isArray(referral?.referrer?.clinic_users) &&
      this.isUserReferrer() &&
      currentClinicName &&
      refName &&
      refName.toUpperCase() === currentClinicName.toUpperCase();

    // update cache
    this._hasMatchCache = {
      lastReferral: referral,
      lastUser: currentUser,
      lastResult: result,
    };

    return result;
  }

  getClinic(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinic() : this.getReferrerClinic();
  }

  getClinicId(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinicId() : this.getReferrerClinicId();
  }

  getClinicName(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinicName() : this.getReferrerClinicName();
  }

  getClinicAddress(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinicAddress() : this.getReferrerClinicAddress();
  }

  getClinicOfficePhone(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinicOfficePhone() : this.getReferrerClinicOfficePhone();
  }

  getClinicFax(currentUser) {
    return this.hasMatch(currentUser) ? this.getReceiverClinicFax() : this.getReferrerClinicFax();
  }

  getReferralStatus(currentUser) {
    return this.hasMatch(currentUser) ? this.getReferrerStatus() : this.getReceiverStatus();
  }

  getProvider(currentUser) {
    return this.hasMatch(currentUser) ? this.referral.provider : this.referral.referrer_provider;
  }

  getProviderId(currentUser) {
    return this.getProvider(currentUser)?.id;
  }

  getProviderName(currentUser) {
    return this.getProvider(currentUser)?.display_name;
  }

  isDraft() {
    return this.getReferrerStatus() === 'Draft';
  }

  getReceiverClinic() {
    const provider = this.referral?.provider;
    if (!provider) return '';
    const name = provider.display_name || provider.name || '';
    const specs = Array.isArray(provider.specializations)
      ? provider.specializations.map(s => (s && s.name ? s.name : null)).filter(Boolean)
      : [];
    if (!name) return specs.length ? specs.join(', ') : '';
    return specs.length ? `${name} - ${specs.join(', ')}` : `${name}`;
  }

  getReceiverClinicId() {
    return this.referral.receiver?.id;
  }

  getReferrerClinicId() {
    return this.referral.referrer?.id;
  }

  getReceiverProviderId() {
    console.log(this.referral.provider);
    return this.referral.provider?.id;
  }

  getReceiverClinicName() {
    return this.referral.receiver?.name;
  }

  getReferrerClinicName() {
    return this.referral.referrer?.name;
  }

  getReceiverClinicAddress() {
    return this.referral.receiver?.full_address;
  }

  getReferrerClinicAddress() {
    return this.referral.referrer?.full_address;
  }

  getReferrerClinic() {
    return `${this.referral?.referrer?.name ?? ''}`;
  }

  getLastUpdatedByFullName() {
    return !isEmpty(this.referral.updated_by)
      // ? `${capitalize(this.referral.updated_by.firstname)} ${capitalize(this.referral.updated_by.lastname)}`
      ? `${StringUtils.toTitleCase(this.referral.updated_by.fullname)}`
      : '';
  }

  getComment() {
    return !isEmpty(this.referral.comment) ? this.referral.comment : '';
  }

  getExtraInfo() {
    return !isEmpty(this.referral.extra_info) ? JSON.parse(this.referral.extra_info) : {};
  }

  // getLastUpdatedByName() {
  //     return !isEmpty(this.referral.updated_by)
  //     ? `${capitalize(this.referral.updated_by.firstname)} ${capitalize(this.referral.updated_by.lastname)}`
  //     : '';
  // }

  getReferrerStatus() {
    var status = this.referral.status;

    if (status === 'declined') {
      status = 'Declined';
    } else if (status === 'rec_completed') {
      status = "Scheduled";
    } else if (status === 'ref_completed') {
      status = "Completed";
    }

    return StringUtils.toTitleCase2(status);
  }

  getReceiverStatus() {
    var status = this.referral.status;
    if (status === 'sent') {
      status = 'Received';
    } else if (status === 'declined') {
      status = 'Declined';
    } else if (status === 'ref_completed') {
      if (this.referral.appointment?.appointment_date) {
        status = "Scheduled";
      } else {
        if (this.referral.accepted_at) {
          status = 'Accepted';
        } else {
          status = 'Received';
        }
      }
    } else if (status === 'rec_completed') {
      status = "Completed";
    }
    return capitalize((status.replace('_', ' ')));
  }

  getDateOrReason() {
    let result = '';
    let reason = this.referral.reason;
    if (!isEmpty(reason)) {
      result = reason;
    } else if (!isEmpty(this.referral.appointment?.appointment_date)) {
      result = DateTimeUtils.formatDisplayDateTime(
        this.referral.appointment.appointment_date,
        this.currentClinicTimeFormat,
        false,
      );
    }

    return result;
  }

  isMaintainedAppointment() {
    if (this.referral.appointment) {
      let appointment = this.referral.appointment;

      if (appointment.appointment_details != null) {
        if (appointment.appointment_maintained) {
          return 'Yes';
        } else if (appointment.appointment_details.reason) {
          return 'No, ' + appointment.appointment_details.reason;
        }
      }
    }

    return 'N/A';
  }

  formattedDate() {
    return DateTimeUtils.formatDisplayDateTime(
      this.referral.updated_at,
      this.currentClinicTimeFormat,
      true,
    );
  }

  sentFormattedDate() {
    return !isEmpty(this.referral.sent_at) ?
      DateTimeUtils.formatDisplayDateTime(this.referral.sent_at, this.currentClinicTimeFormat, true) : "";
  }

  receivedFormattedDate() {
    return this.sentFormattedDate();
  }

  getReceiverClinicOfficePhone() {
    var office_phone = '';
    if(!this.referral.receiver) {
      return office_phone;
    }

    if ('extra_info' in this.referral.receiver && !isEmpty(this.referral.receiver?.extra_info)) {
      var extra_info = JSON.parse(JSON.parse(this.referral.receiver?.extra_info));
      office_phone = 'office_phone' in extra_info ? extra_info['office_phone'] : '';
    }
    return office_phone;
  }
  getReceiverClinicFax() {
    var fax = '';
    if (!this.referral.receiver) {
      return fax;
    }

    if ('extra_info' in this.referral.receiver && !isEmpty(this.referral.receiver?.extra_info)) {
      var extra_info = JSON.parse(JSON.parse(this.referral.receiver?.extra_info));
      fax = 'fax' in extra_info ? extra_info['fax'] : '';
    }
    return fax;
  }

  getReferrerClinicOfficePhone() {
    let office_phone = '';
    const extra = this.referral?.referrer?.extra_info;
    if (!isEmpty(extra)) {
      try {
        const parsed = JSON.parse(JSON.parse(extra));
        office_phone = parsed && parsed.office_phone ? parsed.office_phone : '';
      } catch (e) {
        // ignore malformed JSON
      }
    }
    return office_phone;
  }
  getReferrerClinicFax() {
    let fax = '';
    const extra = this.referral?.referrer?.extra_info;
    if (!isEmpty(extra)) {
      try {
        const parsed = JSON.parse(JSON.parse(extra));
        fax = parsed && parsed.fax ? parsed.fax : '';
      } catch (e) {
        // ignore malformed JSON
      }
    }
    return fax;
  }
}
